import { AddButton, Button, CloseButton, IconButton } from '@ui/shared/Button';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  closePopup,
  openPopup,
} from '../../../../data/providers/popupsProvider';
import { IAthlete, IGroup, ITeam } from '../../../../data/types';
import { addTeam, updateTeam } from '../../../../data/writers/team';
import { useEntities } from '../../../../hooks/useEntities';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { Icon } from '../../../shared/Icon';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../../shared/popups/PopupCard';
import { TeamForm } from './TeamForm';

interface IPopupProps {
  onClose: () => void;
  team?: ITeam;
}

const Popup = (props: IPopupProps): React.ReactElement => {
  const isEdit = Boolean(props?.team);

  const [team, setTeam] = React.useState<Partial<ITeam>>(
    isEdit
      ? props.team
      : {
          name: '',
          image: null,
        }
  );
  const [selectedGroups, setSelectedGroups] = React.useState<IGroup[]>([]);
  const [selectedAthletes, setSelectedAthletes] = React.useState<IAthlete[]>(
    []
  );
  const [saving, setSaving] = React.useState<boolean>(false);
  const [aggregateOnCreate, setAggregateOnCreate] =
    React.useState<boolean>(false);

  /// subscribe to entities
  const { athletes, teams, groups, loading } = useEntities();

  /**
   * @description set the selected groups and athletes based on the supplied team
   */
  React.useEffect((): void => {
    if (props.team && !loading) {
      const mSelectedGroups = groups.filter((g: IGroup): boolean =>
        g.teams?.includes(props.team.id)
      );
      const mSelectedAthletes = athletes.filter((a: IAthlete): boolean =>
        a.teams?.includes(props.team.id)
      );
      setSelectedGroups(mSelectedGroups);
      setSelectedAthletes(mSelectedAthletes);
    }
  }, [loading]);

  /**
   * @description when the selected groups change, remove any selected athletes that are not members
   * of the currently selected groups (unless no groups are selected)
   */
  React.useEffect((): void => {
    if (selectedGroups.length === 0) return;
    const groupIds = selectedGroups.map((g: IGroup): string => g.id);
    const filteredAthletes = selectedAthletes.filter(
      (athlete: IAthlete): boolean =>
        athlete.groups?.some((groupId: string): boolean =>
          groupIds.includes(groupId)
        )
    );
    if (filteredAthletes.length !== selectedAthletes.length) {
      setSelectedAthletes(filteredAthletes);
    }
  }, [selectedGroups.length]);

  /**
   * @description the list of athletes that are available to select because
   * they are members of at least one selected group (or all athletes if no groups selected)
   */
  const athletesAvailableToSelect: IAthlete[] = React.useMemo((): IAthlete[] => {
    if (selectedGroups.length === 0) return athletes;
    return athletes.filter((a: IAthlete): boolean =>
      a.groups?.some((id: string): boolean =>
        selectedGroups.map((g: IGroup): string => g.id).includes(id)
      )
    );
  }, [athletes?.length, selectedGroups]);

  /**
   * @description handles changes to the team
   * @param name
   * @param value
   */
  const onChange = (name: string, value: string): void => {
    const changedTeam = {
      ...team,
      [name]: value,
    };
    setTeam(changedTeam);
  };

  /**
   * @description handles updating the group to the selected teams, and updates athlete membership
   */
  const update = async (): Promise<void> => {
    setSaving(true);
    const teamData = {
      ...team,
    };
    await updateTeam(
      teamData,
      selectedAthletes,
      athletes,
      selectedGroups,
      groups
    );
    setSaving(false);
    props.onClose();
  };

  /**
   * @description handles creating the group to the selected teams, and any selected athletes to the group
   */
  const create = async (): Promise<void> => {
    setSaving(true);
    const teamData = {
      ...team,
    };
    await addTeam(
      teamData,
      selectedAthletes,
      selectedGroups,
      aggregateOnCreate
    );
    setSaving(false);
    props.onClose();
  };

  /**
   * @description handles the selection/de-selection of athletes
   * @param id
   * @param checked
   */
  const athleteSelectionHandler = (athlete: ITeam, checked: boolean): void => {
    let mSelectedAthletes = [...selectedAthletes];
    if (checked) {
      mSelectedAthletes.push(athlete);
    } else {
      mSelectedAthletes = mSelectedAthletes.filter(
        (a: IAthlete): boolean => a.id !== athlete.id
      );
    }
    setSelectedAthletes(mSelectedAthletes);
  };

  /**
   * @description handles the selection/de-selection of a group
   * @param id
   * @param checked
   */
  const groupSelectionHandler = (group: IGroup, checked: boolean): void => {
    let mSelectedGroups = [...selectedGroups];
    if (checked) {
      mSelectedGroups.push(group);
    } else {
      mSelectedGroups = mSelectedGroups.filter(
        (g: IGroup): boolean => g.id !== group.id
      );
    }
    setSelectedGroups(mSelectedGroups);
  };

  /**
   * @description selects all the available athletes
   */
  const selectAllGroups = (): void => setSelectedGroups(groups);
  /**
   * @description clears the currently selected athletes
   */
  const clearAllGroups = (): void => setSelectedGroups([]);

  /**
   * @description selects all the available athletes
   */
  const selectAllAthletes = (): void => setSelectedAthletes(athletesAvailableToSelect);

  /**
   * @description clears the currently selected athletes
   */
  const clearAllAthletes = (): void => setSelectedAthletes([]);

  /**
   * @description determines if the team name already exists
   */
  const hasFormError = React.useMemo((): string | null => {
    const duplicate: ITeam = teams?.find((t: ITeam): boolean => {
      return (
        t.name.toLowerCase() === team?.name.toLowerCase() && t.id !== team.id
      );
    });
    return Boolean(duplicate) ? 'A team with this name already exists' : null;
  }, [team?.name, teams]);

  return (
    <PopupCard onClose={props.onClose} size='large' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>{props?.team ? 'Edit Team' : 'Add Team'}</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {saving && <LinearLoading />}
          <TeamForm
            error={hasFormError}
            onChange={onChange}
            team={team}
            teams={teams}
            groups={groups}
            selectedGroups={selectedGroups}
            clearAllGroups={clearAllGroups}
            selectAllGroups={selectAllGroups}
            groupSelectionHandler={groupSelectionHandler}
            athletes={athletesAvailableToSelect}
            selectedAthletes={selectedAthletes}
            clearAllAthletes={clearAllAthletes}
            selectAllAthletes={selectAllAthletes}
            athleteSelectionHandler={athleteSelectionHandler}
            aggregateOnCreate={aggregateOnCreate}
            setAggregateOnCreate={setAggregateOnCreate}
            isEdit={isEdit}
          />
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <Button
              title='Save Team'
              type='submit'
              border={'none'}
              color={'light'}
              flavor={'primary'}
              onClick={isEdit ? update : create}
              disabled={saving || Boolean(hasFormError)}
              icon='check'
              style={{ whiteSpace: 'nowrap' }}
            />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};
interface IProps extends RouteComponentProps {
  team?: ITeam;
}

const ManageTeamPopup = withRouter((props: IProps): React.ReactElement => {
  const isEdit = Boolean(props.team);
  const openDialog = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    openPopup(
      <Popup onClose={closePopup(props.history)} team={props.team} />,
      props.history,
      isEdit ? `team-edit-${props.team?.id}` : 'team-add'
    );
  };

  return isEdit ? (
    <IconButton icon='edit' onClick={openDialog} title='Edit Team' />
  ) : (
    <AddButton onClick={openDialog} />
  );
});

export { ManageTeamPopup };
